import React, { useRef, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Grid } from "@mui/material";
import { Card, Button, Box, Form, MenuItem, SelectField } from "@curves/ui-base";
import { ContractMemberInfo, ContractMainSection, ContractAgreement, ContractInvoice, ContractPayment, ContractNote } from "@icoach/smartFitnessBand/components/ContractApplyComponents";
import { refIsRequiredError } from "@util/utils";
import { getNowTime, parseDate } from "@util/moment";
import { useSmartFitnessContext } from "@icoach/smartFitnessBand/components/SmartFitnessContext";
import useDeviceOrderApi from "@apis/useDeviceOrderApi";
import { CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import { EmployeeSelect } from "@icoach/components";
import useShowPDF from "@util/hook/useShowPDF";

const SmartFitnessBandContractApply = (props, ref) => {
	const [errorModule, setErrorModule] = useState(null);
	const [disabledButton, setDisabledButton] = useState(true);
	const history = useHistory();
	const showPDF = useShowPDF();
	const { contractApplySourceData, setApplySourceData, options } = useSmartFitnessContext();
	const { employeeOptions = [], deviceTimeTypeOptions = [] } = options;
	const { postDeviceOrderApplyApi, postDeviceOrderRangeFeeApi } = useDeviceOrderApi();
	const contractMemberInfoRef = useRef(null);
	const contractMainSectionRef = useRef(null);
	const contractPaymentRef = useRef(null);
	const contractAgreementRef = useRef(null);
	const contractNoteRef = useRef(null);
	const employeeIDRef = useRef(null);
	const deviceTimeTypeRef = useRef(null);

	const getParams = () => {
		const contractMemberInfoParams = contractMemberInfoRef.current.getResult();
		const contractMainSectionParams = contractMainSectionRef.current.getResult();
		const contractPaymentParams = contractPaymentRef.current.getResult();
		const contractAgreementParams = contractAgreementRef.current.getResult();
		const contractNoteParams = contractNoteRef.current.getResult();

		const {
			plan = {
				planID: 0,
				planType: 0,
			},
		} = contractMainSectionParams;
		const { payment, cardSignImg, paymentType } = contractPaymentParams;
		const { applyDate, mainSection, contractVersion } = contractApplySourceData;
		const { notice, sign } = contractAgreementParams;
		return {
			employeeID: employeeIDRef.current.getResult(),
			deviceTimeType: deviceTimeTypeRef.current.getResult(),
			applyDate: applyDate,
			payment: {
				paymentType: paymentType,
				cardExpiryDate: payment.cardExpiryDate,
				cardRelation: payment.cardRelation,
				creditCardNo: payment.creditCardNo,
				creditCardBankName: payment.creditCardBankName,
				cardSignImg: cardSignImg || sign,
				cardName: payment.cardName,
				creditCardType: payment.creditCardType,
			},
			checkoutPaymentType: contractMainSectionParams.checkoutPaymentType,

			monthFee: mainSection.monthFee,
			trainingFee: mainSection.trainingFee,
			firstMonthFee: mainSection.firstMonthFee,
			plan: {
				planID: plan.planID,
				planType: plan.planType,
			},
			reviewStartDate: parseDate(mainSection.reviewStartDate),
			reviewEndDate: parseDate(mainSection.reviewEndDate),
			subscriptionStartDate: parseDate(mainSection.subscriptionStartDate),
			contractVersion: contractVersion,
			signImg: sign,
			debitFirstDate: parseDate(mainSection.debitFirstDate),
			note: contractNoteParams,
			survey: {
				items: [...notice],
			},
			...contractMemberInfoParams,
		};
	};

	const handleSubmit = e => {
		if (!refIsRequiredError(contractMemberInfoRef, contractMainSectionRef, contractPaymentRef, contractAgreementRef, contractNoteRef, employeeIDRef, deviceTimeTypeRef)) {
			const params = getParams();
			doApplyContractApi(params);
		}
	};

	const doApplyContractApi = async params => {
		const resp = await postDeviceOrderApplyApi(params);
		if (resp) {
			if (resp.isError) {
				setErrorModule(resp.result);
			} else {
				const { completedPath, contractID } = resp;
				const routerPath = CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.CONTRACT_DETAIL_MAIN_PATH(contractID);
				showPDF({
					open: true,
					pdfUrl: completedPath,
				});
				history.push(routerPath);
			}
		}
	};

	const getRangeFeeApi = async (planID, planType) => {
		const resp = await postDeviceOrderRangeFeeApi({
			applyDate: getNowTime(),
			plan: {
				planID: planID,
				planType: planType,
			},
		});
		// 只更新 getRangeFeeApi 的資料
		if (resp) {
			setApplySourceData(resp, true);
		}
	};

	useEffect(
		() => {
			if (contractMemberInfoRef.current) {
				const canUseDevice = contractMemberInfoRef.current.canUseDevice;
				setDisabledButton(canUseDevice === false);
			} else {
				setDisabledButton(true);
			}
		},
		// eslint-disable-next-line
		[contractApplySourceData]
	);

	return (
		<Form onSubmit={handleSubmit}>
			<Box component="header" className="page-header"></Box>
			<Card className="mb-4">
				<ContractMemberInfo ref={contractMemberInfoRef} />
				<ContractMainSection ref={contractMainSectionRef} getRangeFeeApi={getRangeFeeApi} />
				<ContractPayment ref={contractPaymentRef} errorModule={errorModule} />
				<ContractInvoice />
				<ContractNote ref={contractNoteRef} />
			</Card>
			<Card>
				<ContractAgreement ref={contractAgreementRef} className="mb-4" />
			</Card>
			{/* 經辦教練與訂閱時間點 */}
			<Card className="mt-4">
				<Card.CardContent className="p-4">
					<Grid spacing={2} container>
						<Grid xs={6} item>
							<EmployeeSelect key={contractApplySourceData.memberInfo.memberID} ref={employeeIDRef} label="經辦教練" options={employeeOptions} fullWidth required />
						</Grid>
						<Grid xs={6} item>
							<SelectField ref={deviceTimeTypeRef} label="訂閱時間點" defaultValue="" displayEmpty fullWidth required>
								{deviceTimeTypeOptions.map(option => (
									<MenuItem key={option.value} value={option.value} disabled={option.disabled}>
										{option.text}
									</MenuItem>
								))}
							</SelectField>
						</Grid>
					</Grid>
				</Card.CardContent>
				<Box className="text-right px-4 pb-3">
					<Button variant="contained" type="submit" disabled={disabledButton}>
						確認送出
					</Button>
				</Box>
			</Card>
		</Form>
	);
};
export default SmartFitnessBandContractApply;
